import { Link, useLocation } from "react-router-dom";
import { Spinner } from "@/components/ui/spinner";
import { ChevronRight, Megaphone } from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuAction,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { useDashboardStore, useSidebarGroup } from "@/stores";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { TextShimmer } from "@/components/loading-ui/text-shimmer";
import { isRepopulatingCampaign } from "@/lib/eve";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { NavMorePopover } from "./nav-more-popover";

/**
 * One campaign row. POPULATING campaigns (creator search still running)
 * render disabled with a spinner instead of being hidden — they become
 * clickable the moment the roster lands (DashboardDataProvider polls).
 */
function CampaignRow({
  campaign,
  active,
}: {
  campaign: {
    _id: string;
    campaign_name: string;
    status?: string;
    repopulating_since?: string | null;
  };
  active: boolean;
}) {
  if (campaign.status === "POPULATING") {
    return (
      <SidebarMenuSubButton
        size="sm"
        aria-disabled
        className="pointer-events-none [&>svg]:size-3"
      >
        <TextShimmer className="truncate">{campaign.campaign_name}</TextShimmer>
        <Spinner className="ml-auto shrink-0 opacity-60" />
      </SidebarMenuSubButton>
    );
  }
  return (
    <SidebarMenuSubButton asChild isActive={active} size="sm" className="[&>svg]:size-3">
      <Link to={`/dashboard/campaigns/${campaign._id}`}>
        <span className="truncate">{campaign.campaign_name}</span>
        {/* Amy v2 re-running the search — loading hint, row stays clickable. */}
        {isRepopulatingCampaign(campaign) && (
          <Spinner className="ml-auto size-3 shrink-0 opacity-60" />
        )}
      </Link>
    </SidebarMenuSubButton>
  );
}

export function CampaignsNav() {
  const location = useLocation();
  const { campaigns } = useDashboardStore();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  // Open state + inline row count come from the user's sidebar prefs
  // (gear next to "Main Menu"); the chevron toggle is remembered.
  const { open, setOpen, itemsShown } = useSidebarGroup("campaigns");

  const isActive =
    location.pathname === "/dashboard/campaigns" ||
    (location.pathname.includes("/dashboard/campaigns") &&
      !location.pathname.includes("create"));

  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <HoverCard openDelay={0} closeDelay={0}>
          <HoverCardTrigger asChild>
            <SidebarMenuButton isActive={isActive}>
              <Megaphone />
              <span>Campaigns</span>
            </SidebarMenuButton>
          </HoverCardTrigger>

          <HoverCardContent
            side="right"
            align="start"
            className="w-56 flex flex-col gap-1 p-2 pt-0.5 rounded-xl max-h-96.5 overflow-auto [scrollbar-color:--alpha(var(--foreground)/30%)_transparent] scrollbar-thin"
            sideOffset={-1}
          >
            <Link
              to="/dashboard/campaigns"
              className="my-2.5 mx-1 mb-1 text-xs font-medium text-muted-foreground hover:text-foreground group"
            >
              Campaigns{" "}
              <ChevronRight className="size-3.5 inline group-hover:translate-x-0.5 transition-transform" />
            </Link>

            <div className="border-b border-dashed w-auto" />

            {campaigns?.map((campaign) => (
              <ScopeWrapper key={campaign._id} color="primary">
                <CampaignRow
                  campaign={campaign}
                  active={location.pathname.includes(campaign._id)}
                />
              </ScopeWrapper>
            ))}
          </HoverCardContent>
        </HoverCard>
      </SidebarMenuItem>
    );
  }

  // itemsShown 0 = the group never expands: no chevron, no sub-list.
  const canExpand = itemsShown > 0 && (campaigns?.length ?? 0) > 0;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Campaigns" isActive={isActive}>
          <Link to="/dashboard/campaigns">
            <Megaphone />
            <span>Campaigns</span>
          </Link>
        </SidebarMenuButton>

        {canExpand && (
          <>
            <CollapsibleTrigger asChild>
              <SidebarMenuAction className="data-[state=open]:rotate-90 hover:bg-primary/10 cursor-pointer rounded-xs">
                <ChevronRight className="text-primary/60 group-hover:text-primary" />
                <span className="sr-only">Toggle</span>
              </SidebarMenuAction>
            </CollapsibleTrigger>

            <CollapsibleContent className="w-full mt-0.5">
              <SidebarMenuSub className="w-full pr-4">
                {campaigns.slice(0, itemsShown).map((campaign) => (
                  <ScopeWrapper key={campaign._id} color="primary">
                    <SidebarMenuSubItem>
                      <CampaignRow
                        campaign={campaign}
                        active={location.pathname.includes(campaign._id)}
                      />
                    </SidebarMenuSubItem>
                  </ScopeWrapper>
                ))}
              </SidebarMenuSub>

              <NavMorePopover count={campaigns.length - itemsShown}>
                {campaigns.slice(itemsShown).map((campaign) => (
                  <ScopeWrapper key={campaign._id} color="primary">
                    <CampaignRow
                      campaign={campaign}
                      active={location.pathname.includes(campaign._id)}
                    />
                  </ScopeWrapper>
                ))}
              </NavMorePopover>
            </CollapsibleContent>
          </>
        )}
      </SidebarMenuItem>
    </Collapsible>
  );
}
